import { Activity, ChevronRight, Clock, Eye, ShieldCheck, TriangleAlert } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { ROLES } from '@/app/nav'
import { useRole } from '@/app/role'
import { useAuth } from '@/lib/auth/auth'
import { PageHeader } from '@/app/shell/PageHeader'
import { useFleet, type FleetRow } from '@/lib/api/hooks'
import { ALERTS } from '@/mocks'
import { riskColor, tok } from '@/charts'
import { Card, KLabel } from '@/ui/card'
import { MetricTile } from '@/ui/metric-tile'
import { PackBadge } from '@/ui/pack-badge'

const SEV_TONE: Record<string, string> = { critical: tok('--ignition'), warning: tok('--flare'), info: tok('--clear') }

export function HomePage() {
  const { role } = useRole()
  const { user } = useAuth()
  const firstName = user?.name?.trim().split(/\s+/)[0] || 'there'
  const nav = useNavigate()
  const { rows, isLoading } = useFleet()

  const cc = (r: FleetRow) => r.indices?.control_confidence_percent ?? 0
  const scored = rows.filter((r) => r.indices?.control_confidence_percent != null)
  const critical = rows.filter((r) => r.indices?.critical_risk_flag)
  const avgCC = scored.length ? Math.round(scored.reduce((a, r) => a + cc(r), 0) / scored.length) : 0
  const attention = [...scored].sort((a, b) => cc(a) - cc(b)).slice(0, 5)
  const roleName = ROLES.find((r) => r.id === role)?.name

  const kpis =
    role === 'assessor'
      ? [
          ['Assigned sites', String(rows.length), tok('--stable'), 'across packs'],
          ['Critical', String(critical.length), critical.length ? tok('--ignition') : tok('--stable'), 'need review'],
          ['Avg control conf.', String(avgCC), riskColor(avgCC), 'scored sites'],
          ['Evidence', 'Live', tok('--clear'), 'AI-mapped'],
        ]
      : role === 'owner'
        ? [
            ['My site conf.', String(cc(scored[0] ?? rows[0])), riskColor(cc(scored[0] ?? rows[0])), scored[0]?.indices?.maturity_band ?? '—'],
            ['Loss probability', `${scored[0]?.indices?.loss_probability_percent ?? 0}%`, tok('--ember'), scored[0]?.indices?.loss_probability_label ?? '—'],
            ['Live alerts', '1', tok('--flare'), 'SCADA'],
            ['Assurance', scored[0]?.indices?.maturity_band ?? '—', riskColor(cc(scored[0] ?? rows[0])), 'trending'],
          ]
        : [
            ['Portfolio sites', String(rows.length), tok('--stable'), 'BESS + DSEAR'],
            ['Critical flags', String(critical.length), critical.length ? tok('--ignition') : tok('--stable'), 'needs review'],
            ['Avg control conf.', String(avgCC), riskColor(avgCC), 'scored sites'],
            ['Open items', String(ALERTS.length), tok('--flare'), 'across sites'],
          ]

  return (
    <>
      <PageHeader
        title={`Good morning, ${firstName}`}
        subtitle={
          <>
            <span>{roleName}</span>
            {role === 'insurer' && (
              <span className="inline-flex items-center gap-1.5 text-clear">
                <Eye className="size-3.5" /> read-only
              </span>
            )}
          </>
        }
      />

      {isLoading ? (
        <Card>
          <KLabel>Loading portfolio…</KLabel>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {kpis.map(([label, value, tone, state]) => (
              <MetricTile key={label} label={label} value={value} tone={tone} state={state} />
            ))}
          </div>

          <div className="mt-5 grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <div className="mb-2 flex items-center gap-2 font-display text-[13px] font-semibold">
                {role === 'owner' ? <Activity className="size-4 text-primary" /> : <TriangleAlert className="size-4 text-primary" />}
                {role === 'assessor' ? 'Your assessments' : role === 'insurer' ? 'Covered sites' : role === 'owner' ? 'Your site' : 'Sites needing attention'}
              </div>
              <Card className="overflow-hidden p-0">
                <table className="w-full text-[13px]">
                  <tbody>
                    {(role === 'owner' ? scored.slice(0, 1) : attention).map((r) => (
                      <tr key={r.id} className="cursor-pointer border-b last:border-b-0 hover:bg-surface-2" onClick={() => nav(`/site/${r.id}/${role === 'owner' ? 'operations' : 'underwriting'}`)}>
                        <td className="px-4 py-3 font-medium">
                          {r.name}
                          {r.indices?.critical_risk_flag && (
                            <span className="ml-2 rounded bg-ignition/[0.14] px-1.5 py-px text-[10.5px] font-semibold text-ignition">critical</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <PackBadge pack={r.pack} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-20 rounded bg-surface-2">
                              <span className="block h-full rounded" style={{ width: `${cc(r)}%`, background: riskColor(cc(r)) }} />
                            </span>
                            <span className="tabular text-[12px] font-semibold" style={{ color: riskColor(cc(r)) }}>{cc(r)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <ChevronRight className="size-4 text-faint" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
              {role === 'owner' && (
                <div className="mt-3 flex gap-2">
                  <button onClick={() => nav(`/site/${(scored[0] ?? rows[0])?.id}/operations`)} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-surface px-3 py-2 text-[13px] font-semibold hover:bg-surface-2">
                    <Activity className="size-4" /> Live operations
                  </button>
                  <button onClick={() => nav(`/site/${(scored[0] ?? rows[0])?.id}/underwriting`)} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-surface px-3 py-2 text-[13px] font-semibold hover:bg-surface-2">
                    <ShieldCheck className="size-4" /> Underwriting
                  </button>
                </div>
              )}
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2 font-display text-[13px] font-semibold">
                <Clock className="size-4 text-primary" /> Recent activity
              </div>
              <Card>
                <div className="space-y-3">
                  {ALERTS.map((a) => (
                    <div key={a.id} className="flex items-start gap-2.5">
                      <TriangleAlert className="mt-0.5 size-4 shrink-0" style={{ color: SEV_TONE[a.severity] }} />
                      <div className="flex-1">
                        <div className="text-[13px]">{a.message}</div>
                        <div className="text-[11.5px] text-faint">{a.site}</div>
                      </div>
                      <span className="text-[11.5px] text-faint">{a.ageMins < 60 ? `${a.ageMins}m` : `${Math.round(a.ageMins / 60)}h`}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </>
      )}
    </>
  )
}
